import React from "react";
import { ArrowRight, MapPinned, Phone, Truck } from "lucide-react";
import { StatusBadge } from "./StatusBadge";

function primaryLabel(job, variant) {
  if (job.status === "assigned") return "Start job";
  if (["accepted", "en_route", "arrived", "in_progress", "issue_reported"].includes(job.status)) return "Continue job";
  return variant === "list" ? "View job" : "Open job";
}

export function DriverJobCard({ job, variant = "list", onOpen, onPrimaryAction }) {
  if (!job) return null;
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${variant === "current" ? "ring-1 ring-sky-200" : ""}`} data-testid={`driver-job-card-${variant}-${job.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sky-700">{variant === "current" ? "Now" : variant === "next" ? "Next" : "Assigned job"}</p>
          <h3 className="mt-2 font-heading text-xl font-semibold tracking-tight text-slate-900">{job.service_type_snapshot?.replace(/_/g, " ") || "Job"}</h3>
          <p className="mt-1 text-sm text-slate-500">{new Date(job.planned_start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
        </div>
        <StatusBadge status={job.status} testId={`driver-job-status-${job.id}`} />
      </div>

      <div className="mt-4 space-y-2 text-sm text-slate-600">
        <p><span className="font-semibold text-slate-900">Customer:</span> {job.customer_name_snapshot || "—"}</p>
        <p><span className="font-semibold text-slate-900">Pickup:</span> {job.pickup_location || "—"}</p>
        <p><span className="font-semibold text-slate-900">Destination:</span> {job.destination || "—"}</p>
        <p className="flex items-center gap-2"><Truck size={14} className="text-sky-700" /> {job.assigned_vehicle_name || job.assigned_vehicle_id || "Vehicle pending"}</p>
        {job.issue_reported && <p className="text-xs font-semibold text-red-700">Issue reported</p>}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <button type="button" onClick={() => onOpen?.(job)} data-testid={`driver-job-open-${job.id}`} className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          View job
        </button>
        <button type="button" onClick={() => onPrimaryAction?.(job)} data-testid={`driver-job-primary-${job.id}`} className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800">
          {primaryLabel(job, variant)} <ArrowRight size={14} />
        </button>
      </div>

      <div className="mt-3 flex gap-2">
        {job.customer_phone_snapshot && (
          <a href={`tel:${job.customer_phone_snapshot}`} data-testid={`driver-job-call-${job.id}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <Phone size={14} /> Call
          </a>
        )}
        {job.pickup_location && (
          <a href={`https://maps.google.com/?q=${encodeURIComponent(job.pickup_location)}`} target="_blank" rel="noreferrer" data-testid={`driver-job-map-${job.id}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <MapPinned size={14} /> Maps
          </a>
        )}
      </div>
    </div>
  );
}